import React, { useState, useEffect } from 'react';
import { api, ApiUser, ApiSound } from '../services/api';
import {
  ShieldAlert,
  Users,
  Radio,
  FileText,
  Sliders,
  X,
} from 'lucide-react';
import { DriveMode, EngineProfileId } from '../types';
import { ENGINE_PROFILES } from '../config/engines';
import { EV_SOUND_PROFILES } from '../config/evProfiles';

interface AdminPortalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: ApiUser | null;
  // Simulation Mode Hooks
  isSimulationActive: boolean;
  onToggleSimulation: (active: boolean) => void;
  simulationSpeed: number;
  onSetSimulationSpeed: (speed: number) => void;
  simulationAcceleration: number;
  onSetSimulationAcceleration: (accel: number) => void;
  onSetEngineProfile: (profileId: EngineProfileId) => void;
  onSetDriveMode: (mode: DriveMode) => void;
  currentSoundId: EngineProfileId;
  currentDriveMode: DriveMode;
}

export const AdminPortal: React.FC<AdminPortalProps> = ({
  isOpen,
  onClose,
  currentUser,
  isSimulationActive,
  onToggleSimulation,
  simulationSpeed,
  onSetSimulationSpeed,
  simulationAcceleration,
  onSetSimulationAcceleration,
  onSetEngineProfile,
  onSetDriveMode,
  currentSoundId,
  currentDriveMode,
}) => {
  const [activeTab, setActiveTab] = useState<'SIMULATION' | 'USERS' | 'SOUNDS' | 'LOGS'>('SIMULATION');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [soundsList, setSoundsList] = useState<ApiSound[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!isOpen || !isAdmin) return;

    const fetchAdminData = async () => {
      try {
        const [usersRes, soundsRes, logsRes] = await Promise.all([
          api.admin.getUsers().catch(() => ({ users: [] })),
          api.sounds.getCatalogue().catch(() => ({ sounds: [] })),
          api.admin.getAuditLogs().catch(() => ({ logs: [] })),
        ]);
        setUsersList(usersRes.users || []);
        setSoundsList(soundsRes.sounds || []);
        setAuditLogs(logsRes.logs || []);
      } catch {
        // Access error
      }
    };
    fetchAdminData();
  }, [isOpen, isAdmin]);

  if (!isOpen) return null;

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 select-none">
        <div className="bg-slate-900 border border-red-500/50 rounded-3xl p-6 max-w-md w-full text-center shadow-2xl">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="font-display font-black text-xl text-white uppercase tracking-wider mb-2">
            RESTRICTED ACCESS
          </h3>
          <p className="text-xs text-slate-400 font-sans leading-relaxed mb-5">
            The REVIVE Admin Portal and Simulation Environment require verified server-side administrator credentials.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-slate-800 text-slate-300 font-mono font-bold text-xs uppercase cursor-pointer"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleToggleFree = async (soundId: string, currentVal: boolean) => {
    try {
      await api.admin.toggleFreeSound(soundId, !currentVal);
      setSoundsList((prev) =>
        prev.map((s) => (s.id === soundId ? { ...s, isFreeGuest: !currentVal, isUnlocked: true } : s))
      );
    } catch {
      // Fallback
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-2xl p-3 md:p-6 select-none">
      <div className="relative w-full max-w-5xl h-[90vh] bg-slate-900 border border-slate-800 rounded-3xl p-5 md:p-8 flex flex-col shadow-2xl overflow-hidden">
        {/* Admin Header */}
        <div className="flex items-center justify-between pb-3.5 border-b border-slate-800 mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.8)] animate-pulse" />
            <div>
              <h2 className="text-lg md:text-xl font-black font-display text-white uppercase tracking-widest">
                REVIVE ADMIN PORTAL
              </h2>
              <span className="text-[10px] font-mono text-amber-400/90 font-bold uppercase">
                Authorized as {currentUser?.displayName} ({currentUser?.role})
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="grid grid-cols-4 gap-2 p-1 bg-slate-950 rounded-xl border border-slate-800 mb-4 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('SIMULATION')}
            className={`py-2 rounded-lg font-mono font-bold text-xs uppercase flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'SIMULATION' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            Simulation Mode
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('SOUNDS')}
            className={`py-2 rounded-lg font-mono font-bold text-xs uppercase flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'SOUNDS' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Radio className="w-3.5 h-3.5" />
            Sound Catalogue
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('USERS')}
            className={`py-2 rounded-lg font-mono font-bold text-xs uppercase flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'USERS' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Users
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('LOGS')}
            className={`py-2 rounded-lg font-mono font-bold text-xs uppercase flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'LOGS' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Audit Logs
          </button>
        </div>

        {/* TAB CONTENTS */}
        <div className="flex-1 overflow-y-auto pr-1">
          {/* 1. SIMULATION MODE (Admin-Only Testing Controls) */}
          {activeTab === 'SIMULATION' && (
            <div className="flex flex-col gap-4">
              {/* Status & Activation Banner */}
              <div className="p-4 rounded-2xl bg-amber-950/40 border border-amber-500/40 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-sm text-white flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    Admin Direct Acoustic Simulation
                  </div>
                  <div className="text-xs text-slate-300 font-sans mt-0.5">
                    Test all ICE and EV sounds without GPS motion. Completely isolated from normal customer experience.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onToggleSimulation(!isSimulationActive)}
                  className={`px-4 py-2 rounded-xl font-mono font-black text-xs uppercase transition-all cursor-pointer ${
                    isSimulationActive
                      ? 'bg-amber-500 text-slate-950 shadow-[0_0_12px_rgba(245,158,11,0.6)]'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {isSimulationActive ? 'SIMULATION ACTIVE' : 'ACTIVATE SIMULATION'}
                </button>
              </div>

              {/* Sliders Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Speed Slider */}
                <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono uppercase text-slate-400">
                      Simulated Speed
                    </span>
                    <span className="text-sm font-mono font-black text-amber-400">
                      {Math.round(simulationSpeed)} km/h
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="220"
                    step="1"
                    disabled={!isSimulationActive}
                    value={simulationSpeed}
                    onChange={(e) => onSetSimulationSpeed(Number(e.target.value))}
                    className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[9px] font-mono text-slate-500 mt-1.5">
                    <span>0 km/h</span>
                    <span>100 km/h</span>
                    <span>220 km/h</span>
                  </div>
                </div>

                {/* Acceleration Slider */}
                <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono uppercase text-slate-400">
                      Simulated Throttle / Acceleration
                    </span>
                    <span className="text-sm font-mono font-black text-amber-400">
                      {simulationAcceleration > 0 ? `+${simulationAcceleration.toFixed(2)}` : simulationAcceleration.toFixed(2)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="-1.0"
                    max="1.0"
                    step="0.05"
                    disabled={!isSimulationActive}
                    value={simulationAcceleration}
                    onChange={(e) => onSetSimulationAcceleration(Number(e.target.value))}
                    className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[9px] font-mono text-slate-500 mt-1.5">
                    <span>-1.0 (Braking)</span>
                    <span>0.0 (Cruise)</span>
                    <span>+1.0 (Full Throttle)</span>
                  </div>
                </div>
              </div>

              {/* Engine Profile Selector inside Admin */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800">
                <div className="text-xs font-mono uppercase text-slate-400 mb-2">
                  Active Acoustic Profile
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.keys(ENGINE_PROFILES).map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSetEngineProfile(id as EngineProfileId)}
                      className={`p-2 rounded-xl text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                        currentSoundId === id
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-slate-900 text-slate-400 hover:text-white'
                      }`}
                    >
                      {id.toUpperCase()}
                    </button>
                  ))}
                  {Object.keys(EV_SOUND_PROFILES).map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSetEngineProfile(id as EngineProfileId)}
                      className={`p-2 rounded-xl text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                        currentSoundId === id
                          ? 'bg-cyan-600 text-white shadow-md'
                          : 'bg-slate-900 text-slate-400 hover:text-white'
                      }`}
                    >
                      {id.replace('ev_', 'EV ').toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Drive Mode Override */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800">
                <div className="text-xs font-mono uppercase text-slate-400 mb-2">
                  Drive Mode Override (Active: {currentDriveMode})
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(['ECO', 'COMFORT', 'SPORT'] as DriveMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => onSetDriveMode(mode)}
                      className={`p-2.5 rounded-xl text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                        currentDriveMode === mode
                          ? mode === 'SPORT'
                            ? 'bg-rose-600 text-white shadow-md'
                            : mode === 'ECO'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'bg-cyan-600 text-white shadow-md'
                          : 'bg-slate-900 text-slate-400 hover:text-white'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 2. SOUND CATALOGUE MANAGEMENT (Toggle Free Sounds) */}
          {activeTab === 'SOUNDS' && (
            <div className="flex flex-col gap-2">
              <div className="text-xs font-mono text-slate-400 mb-2">
                Configure which sound profiles are available to guests for free.
              </div>
              {soundsList.map((s) => (
                <div
                  key={s.id}
                  className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between"
                >
                  <div>
                    <div className="font-display font-bold text-white text-xs md:text-sm">
                      {s.name} ({s.category})
                    </div>
                    <div className="text-[10px] font-mono text-slate-400">{s.tagline}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggleFree(s.id, s.isFreeGuest)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                      s.isFreeGuest
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    {s.isFreeGuest ? 'FREE GUEST' : 'LOCKED PACK'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 3. USERS MANAGEMENT */}
          {activeTab === 'USERS' && (
            <div className="flex flex-col gap-2">
              <div className="text-xs font-mono text-slate-400 mb-2">
                Registered Driver Accounts
              </div>
              {usersList.map((u) => (
                <div
                  key={u.id}
                  className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between"
                >
                  <div>
                    <div className="font-display font-bold text-white text-sm">
                      {u.display_name || 'Driver'} ({u.email})
                    </div>
                    <div className="text-[10px] font-mono text-slate-400">
                      ID: {u.id} • Registered: {new Date(u.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-slate-850 text-cyan-400 border border-cyan-500/30">
                    {u.role}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 4. AUDIT LOGS */}
          {activeTab === 'LOGS' && (
            <div className="flex flex-col gap-2 font-mono text-xs">
              <div className="text-slate-400 mb-1">Recent Security & Admin Events</div>
              {auditLogs.map((l) => (
                <div key={l.id} className="p-2.5 rounded-lg bg-slate-950 border border-slate-800 text-slate-300">
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span className="text-amber-400 font-bold">{l.action}</span>
                    <span>{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-[11px] text-slate-300 mt-1 break-all">
                    {l.details_json || 'No details'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
